import { Avatar, Box, Typography } from '@dtx-company/design-system/src'
import { CameraAltSharp } from '@mui/icons-material'
import { IconButton } from '@mui/material'
import { PlanChipV3 } from '../../../top/v3/DesktopTopNavV3/components/PlanChipV3'
import { Spacer } from '@dtx-company/shared-components/src/components/atoms/Spacer/index'
import React from 'react'
import events from '@dtx-company/inter-app/src/event-tracking/events/nav'
import useTranslation from 'next-translate/useTranslation'

interface ProfileInfoHeaderProps {
  fullName: string
  profilePicture?: string
  orgName?: string
  email?: string
  planLabel?: string
  onClickEditProfilePicture: () => void
  isDesktop?: boolean
}

export const ProfileImageHeader: React.FC<ProfileInfoHeaderProps> = ({
  fullName,
  profilePicture,
  orgName,
  email,
  planLabel,
  onClickEditProfilePicture,
  isDesktop
}) => {
  const { t } = useTranslation('nav')
  const showEmail = fullName !== email

  return (
    <Box
      sx={theme => ({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        width: '100%',
        padding: theme.spacing(0, 4, 4, 4)
      })}
    >
      {orgName && (
        <Box
          sx={theme => ({
            backgroundColor: 'secondary.main',
            width: '100%',
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'center',
            height: 'auto',
            paddingY: theme.spacing(2),
            marginBottom: theme.spacing(4)
          })}
        >
          <Typography variant="labelSmall">{`Organization: ${orgName}`}</Typography>
        </Box>
      )}
      <Box
        // Setting `onClick` handler on the `Box` because the `IconButton` below has too small of a clickable area.
        // (24x24). This still works for keyboard navigation, "clicking" the `IconButton` while focused bubbles up to
        // this `Box`, triggering the click handler.
        onClick={() => {
          isDesktop
            ? events.Platform_Desktop_TopNav_ProfileMenu_Clicked_EditProfilePicture()
            : events.Platform_MobileMoreOptionsSheet_ProfileMenu_Clicked_EditProfilePicture()
          onClickEditProfilePicture()
        }}
        sx={{ position: 'relative', cursor: 'pointer' }}
      >
        <Avatar src={profilePicture} alt={fullName?.length ? fullName : 'avatar'} size="xLarge" />
        <IconButton
          aria-label={t('ProfileMenu.Avatar.Edit')}
          sx={theme => ({
            position: 'absolute',
            bottom: 0,
            right: 0,
            padding: theme.spacing(1),
            width: theme.spacing(6),
            height: theme.spacing(6),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'background.paper',
            boxShadow: 4,
            ':hover': {
              backgroundColor: theme.palette.background.paper
            }
          })}
        >
          <CameraAltSharp fontSize="small" htmlColor="onSurface.secondary" />
        </IconButton>
      </Box>
      <Spacer mb="16px" />
      <Box sx={{ overflow: 'hidden', maxWidth: '100%' }}>
        <Typography variant="subtitleSmall" noWrap>
          {fullName}
        </Typography>
        {showEmail && (
          <Box sx={theme => ({ mt: theme.spacing(1) })}>
            <Typography variant="bodySmall" color="onSurface.secondary" noWrap>
              {email}
            </Typography>
          </Box>
        )}
      </Box>
      <Spacer mb="16px" />
      {planLabel && <PlanChipV3 planLabel={planLabel} />}
    </Box>
  )
}
